import { useMemo } from 'react';

import { type NetworkId, useTokenPairs } from '@/features/tokenPairs';
import { usePageVisible } from '@/lib/hooks/usePageVisible';

import { markPricesByCoin } from './unrealized';

/**
 * How often the marks behind the tape's open-position figures are refreshed.
 *
 * The markets query is shared with the terminal, where a 60s stale time is
 * right for a list of tradeable pairs. It is not right for a PnL figure: a 40x
 * BTC position moves several percent of its own margin inside a minute, so a
 * stale mark would show a number that is confidently wrong rather than roughly
 * right.
 *
 * 10s is a compromise, not a target. One request covers all ~350 markets, and
 * the tape is a page you watch rather than glance at.
 */
const MARKS_REFETCH_MS = 10_000;

/**
 * Mark prices for every market, as one map, for pricing the tape's open rows.
 *
 * Deliberately the SAME React Query key the terminal's market list uses
 * (`tokenPairsQueryOptions`), so this is a refresh cadence on an existing
 * cache entry rather than a second copy of a 350-row payload. React Query
 * takes the shortest interval among live observers, so the faster poll lasts
 * exactly as long as this page is mounted.
 *
 * Polling, not a socket, on purpose: the price WebSocket is per-pair
 * (`/speed/ws/:networkId/:pair`) and the tape shows many coins at once, so a
 * socket per row is the one shape this must not take.
 */
export function useTapeMarks(networkId: NetworkId): Map<string, number> {
    // a hidden tab must not keep polling; the tape's own socket already drops
    // itself on the same signal
    const visible = usePageVisible();

    const { data } = useTokenPairs(networkId, {
        refetchInterval: visible ? MARKS_REFETCH_MS : false,
        // the figure is an estimate anyway — a failed poll should leave the
        // last mark standing, not blank every open row on the page
        retry: false,
    });

    return useMemo(() => markPricesByCoin(data?.markets), [data?.markets]);
}
